import { useEffect, useRef } from 'react';

import { hardRedirectTo } from '../../../utils';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { RegisterResponse } from '../../../../shared/types/routes/auth/v2';

export type RegisterResponseHandlers = {
  /** The `?next=` deep link, used to decide whether the server's `to` outranks first-run project import. */
  next?: string;
  /** A confirmed password whose account has MFA configured: hand off to the challenge step. */
  onMfaRequired: (mfaMethod: MfaMethodType) => void;
  /** A pending email verification: hand off to the verify-code step. */
  onVerificationRequired: () => void;
};

export type RegisterResponseLatch = {
  /** Arm the effect for one settled response. Call immediately before `register.mutate(...)`. */
  markAwaited: () => void;
  /**
   * Disarm it. Backing out of the flow calls this so a late-resolving request can no longer yank the user
   * into onboarding for an account they walked away from.
   */
  cancelAwaited: () => void;
};

/**
 * React to a settled `POST /api/auth/register` response and route from it: an `mfa_required` result hands off
 * to the MFA step, an invite that joined an existing org redirects to the dashboard, a pending verification
 * holds at the verify step, a Cloud registration with an auto-created org lands on project import, and an
 * org-less registration goes to onboarding.
 *
 * Runs from an effect rather than the mutate `onSuccess` callback, for the reason {@link useLoginResponseEffect}
 * documents: React Query invokes that callback before notifying its subscribers, so navigating there updates
 * this page while React Query is still about to update it (an unmounted-update warning under React 17).
 *
 * Unlike the login variant this needs an explicit latch: `register.data` stays latched after it settles, and
 * this page re-renders on its own state changes, so without one a redirect would re-fire on every later
 * render. `markAwaited` arms it per submission; the effect disarms itself once it has routed.
 */
export function useRegisterResponseEffect(
  data: RegisterResponse | undefined,
  { next, onMfaRequired, onVerificationRequired }: RegisterResponseHandlers,
): RegisterResponseLatch {
  const isAwaitedRef = useRef(false);

  useEffect(() => {
    if (!data || !isAwaitedRef.current) {
      return;
    }
    isAwaitedRef.current = false;
    if (data.status === 'mfa_required') {
      onMfaRequired(data.mfaMethod);
      return;
    }
    if (data.joinedExistingOrganization) {
      // Honor the sanitized server-provided redirect (`?next=`), falling back to the dashboard.
      hardRedirectTo(data.to || '/');
    } else if (data.emailVerificationRequired) {
      onVerificationRequired();
    } else if (data.organizationId) {
      // Cloud auto-created an org; honor an explicit `next` deep-link, else land on first-run project import.
      hardRedirectTo(next && data.to ? data.to : '/projects/import');
    } else {
      hardRedirectTo('/onboarding');
    }
  }, [data, next, onMfaRequired, onVerificationRequired]);

  return {
    markAwaited: () => {
      isAwaitedRef.current = true;
    },
    cancelAwaited: () => {
      isAwaitedRef.current = false;
    },
  };
}
